import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { VIDEO_CREDIT_COST } from 'fym-shared/src/music24/music24Plans';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../../music24/components/primitives/SMLButton';
import { formatTime } from '../../musicVideoEditor/timeline/timelineGeometry';
import { useDirectedVideoContext } from '../DirectedVideoContext';
import { priceMessages as messages } from '../messages/priceMessages';
import { DirectedStepLayout } from '../shell/DirectedStepLayout';
import { AlertIcon } from './director/AlertIcon';
import { ProgressTiles } from './make/ProgressTiles';
import { makeClipCounts, sharePercent } from './make/progressRules';
import { directedResultFacts } from './make/resultRules';
import { ResultView } from './make/ResultView';
import { boldPart } from '../../common/richTextChildren';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the price and the make; each part uses its own slice of it */
import styles from './make/Make.module.scss';
import steps from './DirectorSteps.module.scss';
/* eslint-enable css-modules/no-unused-class */
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the directed video; each part uses its own slice of it */
import shell from '../DirectedVideo.module.scss';
/* eslint-enable css-modules/no-unused-class */

// A key from the text of the part: a message with several bold parts has no duplicate key (QA skpr2-3).
const bold = boldPart;

type PhaseState = 'done' | 'now' | 'next';

/**
 * Step 6, Make (design directed-video g1 to g3, j5): the ready clips of the total, the phases, one
 * tile per Veo clip (a still from the middle of a made clip, never the cover art), then the render
 * parts, then the ready video. The data is `SerializedDirectedVideo.progress`, `clips` and
 * `clipEntries`; the realtime event `musicVideo.directed.updated` reads the video again (the
 * wizard polls only while the socket is down). There is no cancel after the confirm.
 */
const ProgressStepComponent: React.FC = () => {
  const intl = useIntl();
  const { video, song, leave } = useDirectedVideoContext();
  const { cutUuid } = video;
  if (video.status === 'ready' && cutUuid !== null) {
    return (
      <DirectedStepLayout>
        <ResultView cutUuid={cutUuid} />
      </DirectedStepLayout>
    );
  }

  // The price of an 8 s clip in the editor: a failed shot can be made again there.
  const cost = VIDEO_CREDIT_COST.aiClip;
  const isRendering = video.status === 'rendering';
  const { progress } = video;
  const total = progress?.clipsTotal ?? video.clips.total;
  const clipCounts = makeClipCounts(video);
  const done = clipCounts.ready;
  const clipsTotal = clipCounts.total;
  const { failed } = video.clips;
  // The credits back: the share of the price of each failed clip by its seconds.
  const facts = directedResultFacts(video);
  const back = facts.backCredits;
  const price = facts.chargedCredits + back;
  const partsTotal = progress?.renderPartsTotal ?? null;
  const partsDone = progress?.renderPartsDone ?? 0;
  const etaSec = progress?.etaSec ?? null;

  let count: React.ReactNode;
  let share: number;
  if (!isRendering) {
    count = (
      <div className={styles.countColumn}>
        <div className={styles.count}>
          <b>{done}</b>
          <span>{intl.formatMessage(messages.clipsMade, { total: clipsTotal })}</span>
        </div>
        {clipCounts.making > 0 && (
          <span className={styles.countMaking}>
            {intl.formatMessage(messages.clipsMaking, { count: clipCounts.making })}
          </span>
        )}
      </div>
    );
    share = sharePercent(done, clipsTotal);
  } else if (partsTotal !== null && partsTotal > 0) {
    count = (
      <div className={styles.count}>
        <b>{partsDone}</b>
        <span>{intl.formatMessage(messages.partsRendered, { total: partsTotal })}</span>
      </div>
    );
    share = sharePercent(partsDone, partsTotal);
  } else {
    count = (
      <div className={styles.count}>
        <span>{intl.formatMessage(messages.renderStarting)}</span>
      </div>
    );
    share = 0;
  }

  let timeLine: string;
  if (etaSec !== null) {
    timeLine = intl.formatMessage(messages.minutesLeft, { minutes: Math.max(1, Math.round(etaSec / 60)) });
  } else {
    timeLine = isRendering ? intl.formatMessage(messages.phaseRender) : intl.formatMessage(messages.thenRender);
  }

  const shotsState: PhaseState = isRendering ? 'done' : 'now';
  const phases: Array<{ key: string; title: string; text: string; state: PhaseState }> = [
    {
      key: 'credits',
      title: intl.formatMessage(messages.phaseCredits),
      text: intl.formatMessage(messages.phaseCreditsValue, { count: price }),
      state: 'done',
    },
    {
      key: 'shots',
      title: intl.formatMessage(messages.phaseShots),
      text: isRendering
        ? intl.formatMessage(messages.phaseShotsDone, { made: total - failed, failed })
        : intl.formatMessage(messages.phaseClipsValue, { done, total: clipsTotal }),
      state: shotsState,
    },
    {
      key: 'cut',
      title: intl.formatMessage(messages.phaseCut),
      text: intl.formatMessage(messages.phaseCutValue),
      state: isRendering ? 'done' : 'next',
    },
    {
      key: 'render',
      title: intl.formatMessage(messages.phaseRender),
      text:
        isRendering && partsTotal !== null && partsTotal > 0
          ? intl.formatMessage(messages.phaseRenderPart, {
              part: Math.min(partsTotal, partsDone + 1),
              total: partsTotal,
            })
          : intl.formatMessage(messages.phaseRenderValue, { length: formatTime(song.durationSec ?? 0) }),
      state: isRendering ? 'now' : 'next',
    },
  ];

  return (
    <DirectedStepLayout>
      <div className={styles.making} data-testid="directed-progress">
        <div className={shell.head}>
          <h2>{intl.formatMessage(isRendering ? messages.renderingTitle : messages.makingTitle)}</h2>
          <p className={clsx(shell.sub, shell.subDesktop)}>
            {intl.formatMessage(isRendering ? messages.renderingText : messages.makingText)}
          </p>
        </div>
        <div className={styles.counter}>
          {count}
          <div className={styles.barColumn}>
            <div className={styles.bar} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={share}>
              <i style={{ width: `${share}%` }} />
            </div>
            <div className={styles.line}>
              <span>{timeLine}</span>
              <b>
                {back > 0
                  ? intl.formatMessage(messages.creditsTakenBack, { count: price - back, back })
                  : intl.formatMessage(messages.creditsTaken, { count: price })}
              </b>
            </div>
          </div>
        </div>
        <div className={styles.phases}>
          {phases.map((phase) => (
            <div
              key={phase.key}
              className={clsx(
                styles.phase,
                phase.state === 'done' && styles.phaseDone,
                phase.state === 'now' && styles.phaseNow
              )}
            >
              <div className={styles.phaseTitle}>
                <i aria-hidden />
                {phase.title}
              </div>
              <span className={styles.phaseText}>{phase.text}</span>
            </div>
          ))}
        </div>
        {total > 0 && <ProgressTiles video={video} />}
        {failed > 0 && (
          <div className={clsx(steps.alert, steps.alertInfo)}>
            <AlertIcon kind="info" />
            <div>{intl.formatMessage(messages.failedShots, { b: bold, count: failed, credits: back, cost })}</div>
          </div>
        )}
        <div className={styles.actions}>
          <span className={shell.hint}>{intl.formatMessage(messages.closeHint)}</span>
          <span className={clsx(shell.grow, shell.desktopOnly)} />
          <SMLButton className={steps.ghost} onClick={leave}>
            {intl.formatMessage(messages.backToSong)}
          </SMLButton>
        </div>
      </div>
    </DirectedStepLayout>
  );
};

export const ProgressStep = TypedMemo(ProgressStepComponent);
